import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { getMerchantCategories } from "features/categories/api/categoriesApi";
import { createProduct, uploadProductImage } from "../api/productsApi";
import { toast } from "react-toastify";
import "css/add-product.css";

export default function AddProductModal({ show, handleClose }) {
  const defaultProduct = {
    catId: "",
    subCatId: "",
    name: "",
    price: "",
    qty: "",
    order: "",
    isVisible: false,
  };

  const [product, setProduct] = useState(defaultProduct);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  const maxImages = 4;

  useEffect(() => {
    if (show) {
      fetchCategories();
      setProduct(defaultProduct);
      setImages([]);
    }
  }, [show]);

  const fetchCategories = async () => {
    try {
      const res = await getMerchantCategories();
      if (res.success) {
        setCategories(res.data);
      } else {
        toast.error("Ангилал татаж чадсангүй");
      }
    } catch (err) {
      console.error(err);
      toast.error("Сервер алдаа");
    }
  };

  const handleUpload = (file) => {
    if (!file) return;
    if (images.length >= maxImages) return;

    const url = URL.createObjectURL(file);
    setImages((prev) => [...prev, { file, url }]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    if (images.length < maxImages) {
      document.getElementById("image-input").click();
    }
  };

  const updateProduct = (field, value) => {
    setProduct((prev) => ({ ...prev, [field]: value }));
  };

  // ----------------------------------
  // SAVE FUNCTION (MAIN LOGIC)
  // ----------------------------------
  const saveProduct = async () => {
    if (!product.catId || !product.name || !product.price) {
      return toast.error("Шаардлагатай талбаруудыг бөглөнө үү.");
    }

    setSaving(true);

    try {
      // 1. Create product (no images)
      const res = await createProduct(product);

      if (!res.success) {
        setSaving(false);
        return toast.error(res.message || "Бараа нэмэхэд алдаа гарлаа.");
      }

      const productId = res.productId;

      // 2. Upload only the first image if exists
      if (images.length > 0 && productId) {
        const firstFile = images[0].file;

        const uploadRes = await uploadProductImage(firstFile, productId);
        if (!uploadRes.success) {
          setSaving(false);
          return toast.error("Барааны зураг upload хийх үед алдаа гарлаа.");
        }
      }

      toast.success("Бараа амжилттай нэмэгдлээ!");
      handleClose();

      // 1 секунд хүлээгээд хуудсыг шинэчлэх
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error(err);
      toast.error("Серверийн алдаа.");
    }

    setSaving(false);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      onExited={() => {
        setProduct(defaultProduct);
        setImages([]);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Бараа нэмэх</Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pt-3 pb-4">
        <Col xs={12} md={4} className="d-flex align-items-end mb-3">
          <Form.Check
            type="switch"
            label="Идэвхтэй"
            checked={product.isVisible}
            onChange={(e) => updateProduct("isVisible", e.target.checked)}
          />
        </Col>

        {/* IMAGE UPLOAD AREA */}
        <div className="upload-area">
          <div
            className={`upload-box ${
              images.length >= maxImages ? "disabled" : ""
            }`}
            onClick={openFileDialog}
          >
            <div>
              <i className="uil uil-image" style={{ fontSize: 28 }}></i>
              <div>Зураг нэмэх</div>
            </div>

            <input
              id="image-input"
              type="file"
              accept="image/jpeg"
              style={{ display: "none" }}
              onChange={(e) => handleUpload(e.target.files[0])}
            />
          </div>

          <div className="image-list">
            {images.map((img, index) => (
              <div className="uploaded-image" key={index}>
                <img src={img.url} alt="uploaded" />
                <div className="remove-btn" onClick={() => removeImage(index)}>
                  ×
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FORM FIELDS */}
        <Row className="g-3 mt-2">
          {/* CATEGORY */}
          <Col xs={12} md={4}>
            <Form.Label>Ангилал</Form.Label>
            <Form.Select
              value={product.catId}
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                updateProduct("catId", selectedId);
                updateProduct("subCatId", ""); // reset subCat
                const parent = categories.find((el) => el.id === selectedId);
                setSubCategories(parent?.children || []);
              }}
            >
              <option value="">Сонгох...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </Form.Select>
          </Col>

          {/* SUB CATEGORY */}
          <Col xs={12} md={4}>
            <Form.Label>Дэд ангилал</Form.Label>
            <Form.Select
              value={product.subCatId}
              onChange={(e) => updateProduct("subCatId", e.target.value)}
              disabled={!product.catId || subCategories.length === 0}
            >
              <option value="">Сонгох...</option>
              {subCategories.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Барааны нэр</Form.Label>
            <Form.Control
              type="text"
              value={product.name}
              onChange={(e) => updateProduct("name", e.target.value)}
            />
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Үнэ</Form.Label>
            <Form.Control
              type="number"
              value={product.price}
              onChange={(e) => updateProduct("price", e.target.value)}
            />
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Үлдэгдэл</Form.Label>
            <Form.Control
              type="number"
              value={product.qty}
              onChange={(e) => updateProduct("qty", e.target.value)}
            />
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Эрэмбэ</Form.Label>
            <Form.Control
              type="number"
              value={product.order}
              onChange={(e) => updateProduct("order", e.target.value)}
            />
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Хаах
        </Button>

        <Button variant="danger" disabled={saving} onClick={saveProduct}>
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              Хадгалж байна...
            </>
          ) : (
            "Хадгалах"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
